import React, { lazy, Suspense, memo } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';

import { ErrorBoundary } from '@monorepo/common';

import { book } from './books';
import JobsAppRedirect from './JobsAppRedirect';
import NoshBrandRoutes from './NoshBrandRoutes';

// Lazy load all page components for code splitting
const ChangePassword = lazy(() => import('../pages/ChangePassword'));
const PublicProfile = lazy(() => import('../pages/PublicProfile'));
const ConfirmEmail = lazy(() => import('../pages/ConfirmEmail'));
const Newsletter = lazy(() => import('../pages/Newsletter'));
const Account = lazy(() => import('../pages/Account'));
const Update = lazy(() => import('../pages/Update'));
const Orders = lazy(() => import('../pages/Orders'));

// Brands pages
const Brands = lazy(() => import('../pages/brands'));
const EditBrand = lazy(() => import('../pages/brands/EditBrand'));
const BrandOptions = lazy(() => import('../pages/brands/BrandOptions'));
const EditProducts = lazy(() => import('../pages/brands/EditProducts'));
const CreateProduct = lazy(() => import('../pages/brands/CreateProduct'));
const CreateSubBrand = lazy(() => import('../pages/brands/CreateSubBrand'));
const EditProductInfo = lazy(() => import('../pages/brands/EditProductInfo'));
const UploadProductPhotos = lazy(() => import('../pages/brands/UploadProductPhotos'));
const EditProductDetails = lazy(() => import('../pages/brands/EditProductDetails'));

const Breweries = lazy(() => import('../pages/breweries'));
const EditBrewery = lazy(() => import('../pages/breweries/EditBrewery'));
const EditBeer = lazy(() => import('../pages/breweries/beers'));

// Other pages
const Help = lazy(() => import('../pages/Help'));
const Invoice = lazy(() => import('../pages/Invoice'));
const InvoiceConfirmEmail = lazy(() => import('../pages/InvoiceConfirmEmail/InvoiceConfirmEmail'));
const InvoiceCompleted = lazy(() => import('../pages/InvoiceCompleted/InvoiceCompleted'));

// Marketplace pages
const MarketplaceListings = lazy(
  () => import('../pages/Marketplace/MarketplaceListings'),
);

// Press Releases pages
const PressReleases = lazy(() => import('../pages/PressReleases'));
const Upgrade = lazy(() => import('../pages/PressReleases/Upgrade'));
const UpgradeSuccess = lazy(() => import('../pages/PressReleases/UpgradeSuccess/UpgradeSuccess'));

// Minimal Suspense fallback — page components handle their own loading states
// (DataTable skeleton, PageShell spinner, etc.)
const LoadingFallback = () => (
  <div style={{ minHeight: '200px' }} />
);

const RootRouters = () => (
  <>
    <Suspense fallback={<LoadingFallback />}>
      <Switch>
        <Route
          exact
          path="/"
        >
          <Redirect to={book.userAccount} />
        </Route>

        <Route
          exact
          path={book.userAccount}
        >
          <ErrorBoundary>
            <Account />
          </ErrorBoundary>
        </Route>

      <Route
        exact
        path={book.userUpdate}
      >
        <ErrorBoundary>
          <Update />
        </ErrorBoundary>
      </Route>

      <Route
        exact
        path={book.userPublicProfile}
      >
        <ErrorBoundary>
          <PublicProfile />
        </ErrorBoundary>
      </Route>

      <Route
        exact
        path={book.userOrders}
      >
        <ErrorBoundary>
          <Orders />
        </ErrorBoundary>
      </Route>

      <Route
        exact
        path={book.userChangePassword}
      >
        <ErrorBoundary>
          <ChangePassword />
        </ErrorBoundary>
      </Route>

      <Route
        exact
        path={book.confirmEmail}
      >
        <ErrorBoundary>
          <ConfirmEmail />
        </ErrorBoundary>
      </Route>

      <Route
        exact
        path={book.newsletter}
      >
        <ErrorBoundary>
          <Newsletter />
        </ErrorBoundary>
      </Route>
      {/* ----------------------------------   Brands */}
      <Route
        exact
        path={book.brands.list}
      >
        <ErrorBoundary>
          <Brands />
        </ErrorBoundary>
      </Route>

      <Route
        exact
        path={book.brands.brandOptions()}
      >
        <ErrorBoundary>
          <BrandOptions />
        </ErrorBoundary>
      </Route>

      <Route
        exact
        path={book.brands.editBrand()}
      >
        <ErrorBoundary>
          <EditBrand />
        </ErrorBoundary>
      </Route>
      <Route
        exact
        path={book.brands.editProducts()}
      >
        <ErrorBoundary>
          <EditProducts />
        </ErrorBoundary>
      </Route>

      <Route
        exact
        path={book.brands.createProduct()}
      >
        <ErrorBoundary>
          <CreateProduct />
        </ErrorBoundary>
      </Route>

      <Route
        exact
        path={book.brands.editProductInfo()}
      >
        <ErrorBoundary>
          <EditProductInfo />
        </ErrorBoundary>
      </Route>

      <Route
        exact
        path={book.brands.uploadProductPhotos()}
      >
        <ErrorBoundary>
          <UploadProductPhotos />
        </ErrorBoundary>
      </Route>

      <Route
        exact
        path={book.brands.editProductDetails()}
      >
        <ErrorBoundary>
          <EditProductDetails />
        </ErrorBoundary>
      </Route>

      <Route
        exact
        path={book.brands.createSubBrand()}
      >
        <ErrorBoundary>
          <CreateSubBrand />
        </ErrorBoundary>
      </Route>

      {/* ----------------------------------   NOSH Brands */}
      <Route path={book.noshBrands.hub()}>
        <NoshBrandRoutes />
      </Route>

      <Route exact path={book.breweries.list}><ErrorBoundary><Breweries /></ErrorBoundary></Route>
      <Route exact path={book.breweries.edit()}><ErrorBoundary><EditBrewery /></ErrorBoundary></Route>
      <Route exact path={book.breweries.newBeer()}><ErrorBoundary><EditBeer /></ErrorBoundary></Route>
      <Route exact path={book.breweries.editBeer()}><ErrorBoundary><EditBeer /></ErrorBoundary></Route>
      {/* ----------------------------------   Help */}
      <Route
        exact
        path={book.needHelp}
      >
        <ErrorBoundary>
          <Help />
        </ErrorBoundary>
      </Route>

      {/* ----------------------------------   Invoice */}
      <Route
        exact
        path={book.invoices.invoice}
      >
        <ErrorBoundary>
          <Invoice />
        </ErrorBoundary>
      </Route>
      <Route
        exact
        path={book.invoices.confirmEmail}
      >
        <ErrorBoundary>
          <InvoiceConfirmEmail />
        </ErrorBoundary>
      </Route>
      <Route
        exact
        path={book.invoices.invoiceCompleted}
      >
        <ErrorBoundary>
          <InvoiceCompleted />
        </ErrorBoundary>
      </Route>

      {/* Retired Account Jobs routes remain as thin saved-link redirects. */}
      <Route
        exact
        path={[
          '/user/jobsApplicants',
          book.jobs.jobsPrepaid,
          book.jobs.jobsPrepaidDetail(),
          book.jobs.jobsApplicants(),
          book.jobs.jobsApplicantDetail(),
          book.jobs.jobsListing,
          book.jobs.postJobListing,
          book.jobs.editJob(),
          book.jobs.reactivateListing(),
          book.jobs.upgradeListing(),
          book.jobs.postPrepaidListing(),
          book.jobs.receipt(),
          book.jobs.noRemaining,
          book.jobs.prepaidExpired,
        ]}
      >
        <JobsAppRedirect />
      </Route>
      {/* ----------------------------------   Marketplace */}
      <Route
        exact
        path={book.marketplace.listings}
      >
        <ErrorBoundary>
          <MarketplaceListings />
        </ErrorBoundary>
      </Route>
      {/* ----------------------------------   Press Releases */}
      <Route
        exact
        path={book.pressReleases.pressReleases}
      >
        <ErrorBoundary>
          <PressReleases />
        </ErrorBoundary>
      </Route>
      <Route
        exact
        path={book.pressReleases.upgrade()}
      >
        <ErrorBoundary>
          <Upgrade />
        </ErrorBoundary>
      </Route>
      <Route
        exact
        path={book.pressReleases.upgradeSuccess}
      >
        <ErrorBoundary>
          <UpgradeSuccess />
        </ErrorBoundary>
      </Route>
      </Switch>
    </Suspense>
  </>
);

export default memo(RootRouters);
